import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import moment from 'moment-timezone';
import React, { useEffect, useRef, useState } from 'react';
import { StructuredTaskSection } from '../../../../common/components/StructuredTaskDetails';
import { Resource } from '../../../../common/constants/resource';
import { MIBCode } from '../../../tasks/types/mib';
import {
  dateToString,
  NORA_DISPLAY_DATE_FORMAT,
} from '../../utilities/formattingUtilities';
import styles from './MIBDetail.module.scss';

const COPY_MIB_CODES_MS = 1800;

export const RenderMIBCodesSection = (mibCodes: MIBCode[]) => {
  const rows = formatData(mibCodes);

  return (
    <StructuredTaskSection title={Resource.MIB_CODES_SUBTITLE}>
      <Table size="small" className={styles.CodesTable}>
        <TableHead>
          <TableRow>
            <TableCell
              className={`${styles.CodesHeadCell} ${styles.CodesDateCol}`}
            >
              <div className={styles.CodesHeadLabel}>
                {Resource.MIB_CODES_DATE}
              </div>
            </TableCell>
            <TableCell
              className={`${styles.CodesHeadCell} ${styles.CodesCodeCol}`}
            >
              <div className={styles.CodesHeadLabel}>
                {Resource.MIB_CODES_CODE}
              </div>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <MIBCodeRow
              key={`mib-codes-row-${index}`}
              date={row.date}
              code={row.code}
            />
          ))}
        </TableBody>
      </Table>
    </StructuredTaskSection>
  );
};

const MIBCodeRow = ({ date, code }: { date: string; code: string }) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${date}: ${code}`);
    } catch {
      return;
    }
    setCopied(true);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => setCopied(false), COPY_MIB_CODES_MS);
  };

  return (
    <TableRow className={styles.CodesRow}>
      <TableCell className={`${styles.CodesCell} ${styles.CodesDateCol}`}>
        <div className={styles.CodesDateValue}>{date}</div>
      </TableCell>
      <TableCell className={`${styles.CodesCell} ${styles.CodesCodeCol}`}>
        <div className={styles.CodeCellInner}>
          <span className={styles.CodeValue}>{code}</span>
          <button
            type="button"
            className={`${styles.CopyBtn} ${
              copied ? styles.CopyBtnCopied : ''
            }`}
            onClick={handleCopy}
            aria-label={`Copy ${date} ${code}`}
          >
            <FontAwesomeIcon
              icon={copied ? faCheck : faCopy}
              className={styles.CopyBtnIcon}
            />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
};

function formatData(mibCodes: MIBCode[]) {
  const formattedTableData = mibCodes.map(mibCode => {
    return {
      date: dateToString(mibCode.date),
      code: mibCode.code,
    };
  });
  formattedTableData.sort((a: { date: string }, b: { date: string }) =>
    moment(b.date, NORA_DISPLAY_DATE_FORMAT).diff(
      moment(a.date, NORA_DISPLAY_DATE_FORMAT),
    ),
  );
  return formattedTableData;
}
